import React, { useEffect, useState } from 'react'
import { Button } from '../../lib/ui/button'
import { Input } from '~/lib/ui/input'
import { Textarea } from '~/lib/ui/textarea'
import {
  TabNav,
  TabPanel,
  PanelContainer,
  ButtonContainer,
  TabButton,
} from '~/components/shared/TabNav'
import PracticeInterviewProvider, {
  usePracticeInterviewing,
} from './PracticeInterviewingProvider'
import { MagicWand } from 'iconoir-react'
import {
  SparklesIcon,
  TrashIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'
import MagnifyingGlassIcon from '~/components/job_search/JobSearchPage/MagnifyingGlassIcon'
import PracticeInterviewForm from './PracticeInterviewForm'
import PracticeInterviewQuestions from './PracticeInterviewQuestions'
import NoSavedJobsImage from '../job_search/SavedJobsPage/NoSavedJobsImage'
import { cn } from '~/lib/utils'
import { TranslationProvider } from '../shared/TranslationProvider'
import { useTranslation } from '../../hooks/useTranslation'
import translations from './locales.json'
import { EditIcon } from '../job_search/AlertsPage/icons'
import { SaveIcon } from 'lucide-react'

const PracticeInterview = (props) => {
  const { locale } = props
  return (
    <PracticeInterviewProvider {...props}>
      <TranslationProvider
        locale={locale || 'en'}
        translations={translations}
        scope={'javascript.components.PracticeInterviewing'}
      >
        <PracticeInterviewInner />
      </TranslationProvider>
    </PracticeInterviewProvider>
  )
}

export default PracticeInterview

const PracticeInterviewInner = () => {
  const {
    showForm,
    setShowForm,
    showInterview,
    generatingInterview,
    showResponses,
    activeTab,
    setInterviewParams,
    setDisableContinue,
  } = usePracticeInterviewing()

  const { t } = useTranslation('index')

  useEffect(() => {
    const jobTitle = localStorage.getItem('jobTitle')
    const company = localStorage.getItem('company')
    const jobDescription = localStorage.getItem('jobDescription')
    if (jobTitle || company || jobDescription) {
      setInterviewParams((prev) => ({
        ...prev,
        job_title: jobTitle,
        company: company,
        job_description: jobDescription,
      }))
      setDisableContinue(!jobTitle || jobTitle.length < 1)
      setShowForm(true)
      localStorage.removeItem('jobTitle')
      localStorage.removeItem('company')
      localStorage.removeItem('jobDescription')
    }
  }, [])

  const handleshowForm = () => {
    setShowForm(true)
  }
  return (
    <>
      <div
        className={
          'flex w-full flex-col pt-8 md:ml-12 md:flex-row ' +
          (!showForm ? 'md:justify-between' : '')
        }
      >
        <div>
          <h1 className="ml-4 md:ml-0">{t('title')}</h1>
          <p className="ml-4 mt-2 md:ml-0">{t('description')}</p>
        </div>
        {!showForm &&
          !showInterview &&
          !generatingInterview &&
          !showResponses && (
            <div className="m-auto md:m-0 md:mr-24">
              <Button size="lg" onClick={handleshowForm}>
                {t('start_practice')}
              </Button>
            </div>
          )}
      </div>

      <div className="flex w-full flex-col p-4 md:flex-row md:p-0">
        {!showForm &&
          !showInterview &&
          !generatingInterview &&
          !showResponses && (
            <TabNav defaultActiveTab={activeTab} className="mx-12">
              <ButtonContainer className="mb-6 flex flex-row justify-between gap-5 min-[530px]:justify-start min-[530px]:gap-10">
                <TabButton
                  name="howDoesPracticeInterviewing"
                  text="How Does Practice Interviewing Help?"
                />
                <TabButton
                  name="previousPracticeInterviews"
                  text="Previous Practice Interviews"
                />
              </ButtonContainer>
              <PanelContainer>
                <TabPanel name="howDoesPracticeInterviewing">
                  <TipsPanel />
                </TabPanel>
                <TabPanel name="previousPracticeInterviews">
                  <PreviousInterviews />
                </TabPanel>
              </PanelContainer>
            </TabNav>
          )}
        {showForm && <PracticeInterviewForm />}
        {(generatingInterview || showInterview || showResponses) && (
          <PracticeInterviewQuestions />
        )}
      </div>
    </>
  )
}

const TipsPanel = () => {
  const { t } = useTranslation('index')
  return (
    <div className="flex h-2/3 flex-col gap-4 p-10 md:grid md:grid-cols-3 md:grid-rows-2 md:gap-x-20 md:gap-y-10">
      <div className="rounded-2xl  bg-lily-blue p-4 text-primary md:max-h-80">
        <SparklesIcon className="inline w-6" />
        <p className="mt-2 !text-lg !font-bold">{t('cards.1.title')}</p>
        <p className="mt-2">{t('cards.1.description')}</p>
      </div>
      <div className="rounded-2xl  bg-lily-blue p-4 text-primary md:max-h-80">
        <MagicWand className="inline w-6" />
        <p className="mt-2 !text-lg !font-bold">{t('cards.2.title')}</p>
        <p className="mt-2">{t('cards.2.description')}</p>
      </div>
      <div className="rounded-2xl  bg-lily-blue p-4 text-primary md:max-h-80">
        <MagnifyingGlassIcon className="inline w-6" />
        <p className="mt-2 !text-lg !font-bold">{t('cards.3.title')}</p>
        <p className="mt-2">{t('cards.3.description')}</p>
      </div>
      <div className="rounded-2xl  bg-lily-blue p-4 text-primary md:max-h-80">
        <SparklesIcon className="inline w-6" />
        <p className="mt-2 !text-lg !font-bold">{t('cards.4.title')}</p>
        <p className="mt-2">{t('cards.4.description')}</p>
      </div>
      <div className="rounded-2xl  bg-lily-blue p-4 text-primary md:max-h-80">
        <SparklesIcon className="inline w-6" />
        <p className="mt-2 !text-lg !font-bold">{t('cards.5.title')}</p>
        <p className="mt-2">{t('cards.5.description')}</p>
      </div>
      <div className="rounded-2xl  bg-lily-blue p-4 text-primary md:max-h-80">
        <SparklesIcon className="inline w-6" />
        <p className="mt-2 !text-lg !font-bold">{t('cards.6.title')}</p>
        <p className="mt-2">{t('cards.6.description')}</p>
      </div>
    </div>
  )
}

const PreviousInterviews = () => {
  const {
    practiceInterviews,
    deletePracticeInterview,
    setShowResponses,
    getInterviewResponses,
    setActiveTab,
    setDocumentId,
    setShowForm,
    practiceInterviewAiRequests,
    resumes,
    updateInterview,
  } = usePracticeInterviewing()
  const [expandedRow, setExpandedRow] = useState(null)
  const [interviewId, setInterviewId] = useState(null)
  const [interviewName, setInterviewName] = useState('')

  const { t } = useTranslation('PreviousInterviews')

  const getCreatedAt = (date) => {
    const timestamp = new Date(date)
    return (
      timestamp.getMonth() +
      1 +
      '/' +
      timestamp.getDate() +
      '/' +
      timestamp.getFullYear()
    )
  }

  const handleDeleteItem = async (interview, e) => {
    e.preventDefault()
    window.confirm(
      'Are you sure you want to remove this interview from your saved interviews?',
    ) && deletePracticeInterview(interview)
  }

  const goToInterviewRepsonses = async (id, e) => {
    e.preventDefault()
    setShowResponses(true)
    setActiveTab('previousPracticeInterviews')
    setDocumentId(id)
    await getInterviewResponses(id)
  }

  const handleshowForm = () => {
    setShowForm(true)
    setActiveTab('previousPracticeInterviews')
  }

  const handleExpand = (index) => {
    if (expandedRow === index) {
      setExpandedRow(null)
    } else {
      setExpandedRow(index)
    }
  }

  return (
    <div className="mt-8 rounded-lg border border-alto-200 md:m-8 md:rounded-2xl">
      <div className="relative w-full overflow-auto rounded-lg md:rounded-2xl">
        <table className="w-full caption-bottom rounded-lg text-sm md:rounded-2xl">
          <thead className="rounded-lg bg-intoo-blue-medium md:rounded-2xl [&_tr]:border-b">
            <tr className="overflow rounded-lg border-b border-alto-200 bg-intoo-blue-medium p-4 text-white transition-colors hover:bg-intoo-blue-medium data-[state=selected]:bg-muted sm:px-8 sm:py-6 md:rounded-2xl">
              <th className="job-search-table-header h-16 w-1/2 px-4 text-left align-middle font-roboto text-lg font-bold text-foreground data-[col-id=status]:w-[96px] sm:h-16 sm:px-4 data-[col-id=status]:sm:w-[120px] md:w-auto [&:has([role=checkbox])]:pr-0">
                {t('interview')}
              </th>
              <th className="job-search-table-header h-16 px-4 pl-0 text-left align-middle font-roboto text-lg font-bold text-foreground data-[col-id=status]:w-[96px] sm:h-16 sm:px-4 data-[col-id=status]:sm:w-[120px] [&:has([role=checkbox])]:pr-0">
                {t('date')}
              </th>
              <th className="job-search-table-header h-16 px-4 pl-0 text-left align-middle font-roboto text-lg font-bold text-foreground data-[col-id=status]:w-[96px] sm:h-16 sm:px-4 data-[col-id=status]:sm:w-[120px] [&:has([role=checkbox])]:pr-0"></th>
              <th className="job-search-table-header h-16 px-4 pl-0 text-left align-middle font-roboto text-lg font-bold text-foreground data-[col-id=status]:w-[96px] sm:h-16 sm:px-4 data-[col-id=status]:sm:w-[120px] [&:has([role=checkbox])]:pr-0"></th>
              <th className="job-search-table-header h-16 px-4 pl-0 text-left align-middle font-roboto text-lg font-bold text-foreground data-[col-id=status]:w-[96px] sm:h-16 sm:px-4 data-[col-id=status]:sm:w-[120px] [&:has([role=checkbox])]:pr-0"></th>
            </tr>
          </thead>

          <tbody className="[&_tr:last-child]:border-0">
            {practiceInterviews.map((interview, idx) => {
              const aiRequest =
                practiceInterviewAiRequests.find(
                  (request) => request?.document_id === interview?.id,
                ) || {}
              const resume = resumes.find(
                (resume) =>
                  resume?.id === parseInt(aiRequest?.request_params?.resume_id),
              )
              const resumeName = resume ? resume.name : 'N/A'
              const resumeId = resume ? resume.id : ''

              return (
                <>
                  <tr
                    className={
                      'border-b border-alto-200 transition-colors' +
                      (idx % 2 === 0
                        ? 'bg-white hover:bg-white data-[state=selected]:bg-white'
                        : 'bg-[#e2e2e2] bg-opacity-30 hover:bg-[#e2e2e2] hover:bg-opacity-30 data-[state=selected]:bg-[#e2e2e2] data-[state=selected]:bg-opacity-30')
                    }
                    key={'interviewRow_' + idx}
                  >
                    <td className="flex w-1/2 flex-row items-center p-4 pb-2 pr-0 pt-3 align-middle sm:p-4 sm:pr-1 md:w-auto [&:has([role=checkbox])]:pr-0">
                      {interview.id !== interviewId && (
                        <>
                          <a
                            onClick={(e) =>
                              goToInterviewRepsonses(interview.id, e)
                            }
                          >
                            {interview.name}
                          </a>
                          <a
                            className="ml-2"
                            onClick={() => {
                              setInterviewId(interview.id)
                              setInterviewName(interview.name)
                            }}
                          >
                            <EditIcon className="ml-2 inline h-4 w-4 cursor-pointer text-primary" />
                          </a>
                        </>
                      )}
                      {interview.id === interviewId && (
                        <>
                          <Input
                            className="w-3/4"
                            value={interviewName || interview.name}
                            onChange={(e) => {
                              setInterviewName(e.target.value)
                            }}
                          />
                          <a
                            className="ml-2"
                            onClick={() => {
                              setInterviewId(null)
                              setInterviewName(interviewName)
                              updateInterview(interview, interviewName)
                            }}
                          >
                            <SaveIcon className="ml-2 inline h-4 w-4 cursor-pointer text-primary" />
                          </a>
                        </>
                      )}
                    </td>
                    <td className="p-4 pb-2 pl-0 pr-0 pt-3 align-middle sm:p-4 sm:pr-1 [&:has([role=checkbox])]:pr-0">
                      {getCreatedAt(interview.created_at)}
                    </td>
                    <td className="p-4 pb-2 pl-0 pr-0 pt-3 align-middle sm:p-4 sm:pr-1 [&:has([role=checkbox])]:pr-0">
                      <Button
                        variant="outline"
                        onClick={(e) => goToInterviewRepsonses(interview.id, e)}
                      >
                        {t('view_interview')}
                      </Button>
                    </td>
                    <td className="p-4 pb-2 pl-0 pr-0 pt-3 align-middle sm:p-4 sm:pr-1 [&:has([role=checkbox])]:pr-0">
                      <Button
                        className="w-44"
                        onClick={() => handleExpand(idx)}
                      >
                        {expandedRow === idx
                          ? t('hide_details')
                          : t('show_details')}{' '}
                        <ChevronDownIcon
                          className={cn(
                            'ml-auto inline-block h-4 w-4 stroke-2 transition-all duration-500',
                            expandedRow === idx && 'rotate-180',
                          )}
                        />
                      </Button>
                    </td>
                    <td className="p-4 pb-2 pl-0 pr-0 pt-3 align-middle sm:p-4 sm:pr-1 [&:has([role=checkbox])]:pr-0">
                      <TrashIcon
                        className="w-6 cursor-pointer bg-transparent text-red-500 hover:bg-transparent hover:text-red-400"
                        onClick={(e) => handleDeleteItem(interview, e)}
                      />
                    </td>
                  </tr>
                  {expandedRow === idx && (
                    <tr className="border-b border-alto-200 transition-colors">
                      <td
                        colSpan={5}
                        className="table-cell py-8 pr-0 text-center align-middle md:pl-4 [&:has([role=checkbox])]:pr-0"
                      >
                        <div className="flex flex-col gap-4">
                          <p className="text-left font-bold">
                            {t('interview_details')}
                          </p>
                          <div className="flex flex-row gap-4">
                            <div className="flex flex-col">
                              <p>{t('uploaded_resume')}</p>
                              <Button disabled={!resume}>
                                <a
                                  className="text-white"
                                  href={`/resume/builder/${resumeId}`}
                                >
                                  {resume ? resumeName : 'N/A'}
                                </a>
                              </Button>
                            </div>
                            <div className="flex flex-col">
                              <label>{t('number_of_questions')}</label>
                              <Input
                                disabled
                                value={
                                  practiceInterviewAiRequests[idx]
                                    ?.request_params?.number_of_questions ||
                                  'N/A'
                                }
                              />
                            </div>
                            <div className="flex flex-col">
                              <label>{t('mode')}</label>
                              <Input
                                disabled
                                value={
                                  practiceInterviewAiRequests[idx]
                                    ?.request_params?.practice_mode === 'true'
                                    ? t('practice')
                                    : practiceInterviewAiRequests[idx]
                                          ?.request_params?.practice_mode ===
                                        'false'
                                      ? t('real')
                                      : 'N/A'
                                }
                              />
                            </div>
                            <div className="flex flex-col">
                              <label>{t('type_of_interview')}</label>
                              <Input
                                disabled
                                value={
                                  practiceInterviewAiRequests[idx]
                                    ?.request_params?.audio_interview === 'true'
                                    ? t('audio')
                                    : practiceInterviewAiRequests[idx]
                                          ?.request_params?.audio_interview ===
                                        'false'
                                      ? t('video')
                                      : 'N/A'
                                }
                              />
                            </div>
                          </div>
                          <p className="text-left font-bold">
                            {t('position_details')}
                          </p>
                          <div className="flex flex-row gap-4">
                            <div className="flex flex-col">
                              <label>{t('position_title')}</label>
                              <Input
                                disabled
                                value={
                                  practiceInterviewAiRequests[idx]
                                    ?.request_params?.job_title || 'N/A'
                                }
                              />
                            </div>
                            <div className="flex flex-col">
                              <label>{t('company')}</label>
                              <Input
                                disabled
                                value={
                                  practiceInterviewAiRequests[idx]
                                    ?.request_params?.company || 'N/A'
                                }
                              />
                            </div>
                            <div className="flex flex-col">
                              <label>{t('industry')}</label>
                              <Input
                                disabled
                                value={
                                  practiceInterviewAiRequests[idx]
                                    ?.request_params?.industry || 'N/A'
                                }
                              />
                            </div>
                          </div>
                          <div className="flex w-full flex-row">
                            <div className="flex w-3/4 flex-col">
                              <label className="text-left">
                                {t('job_description')}
                              </label>
                              <Textarea
                                className="h-48"
                                disabled
                                value={
                                  practiceInterviewAiRequests[idx]
                                    ?.request_params?.job_description || 'N/A'
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              )
            })}
            {!practiceInterviews ||
              (practiceInterviews.length === 0 && (
                <tr className="border-b border-alto-200 transition-colors">
                  <td
                    colSpan={5}
                    className="table-cell py-16 pr-0 text-center align-middle md:pl-4 [&:has([role=checkbox])]:pr-0"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <NoSavedJobsImage />
                      <div className="max-w-96 text-center">
                        <h2 className="p-8">{t('no_practice_interviews')}</h2>
                        <p className="text-lg">
                          {t('start_your_practice_interview')}{' '}
                          <a onClick={() => handleshowForm()}>{t('here')}</a>
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
